import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Modal, Input, InputNumber, Button } from "antd";
import { toast } from "react-toastify";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import { AI_TIMEOUT, startAILoading } from "api/aiLoading";
import {
  InterviewRoundStatusEnum,
  InterviewRoundStatusLabel,
  InterviewRoundStatusColor,
} from "Constants/Enums/InterviewRoundStatusEnum";
import { InterviewRoundTypeLabel } from "Constants/Enums/InterviewRoundTypeEnum";
import {
  Card,
  Pill,
  LabelMeta,
  Divider,
  LoadingState,
  EmptyState,
  HighlightCard,
  theme,
  formatDateTime,
} from "../components/ui";

function InterviewRoundsTab({
  jobApplicationId,
  isRejected = false,
  isPostingFilled = false,
}) {
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [proposeRound, setProposeRound] = useState(null);
  const [completeRound, setCompleteRound] = useState(null);

  const fetchInterviewRounds = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        API.hiringInterviews.getRoundsByApplication(jobApplicationId)
      );
      setRounds(response.data || []);
    } catch (error) {
      console.error("Error fetching interview rounds:", error);
      if (error.response?.status !== 404) {
        toast.error("Failed to load interview rounds");
      }
      setRounds([]);
    } finally {
      setLoading(false);
    }
  }, [jobApplicationId]);

  useEffect(() => {
    if (!jobApplicationId) return;
    fetchInterviewRounds();
  }, [jobApplicationId, fetchInterviewRounds]);

  // Rejected → no actions, no list view either
  if (isRejected) {
    return (
      <EmptyState
        icon="🚫"
        title="Candidate rejected"
        hint="Interview procedures are disabled for rejected applications."
      />
    );
  }

  if (loading) return <LoadingState label="Loading interview rounds..." />;

  if (!rounds || rounds.length === 0) {
    return (
      <EmptyState
        icon="🎯"
        title="No interview rounds yet"
        hint="Once HR adds rounds for this application, they will appear here."
      />
    );
  }

  // Per Hammad: rounds named "Hiring Committee", "Final Decision", "Offer" are
  // decision stages, not interview rounds. Split them out so HR sees the
  // interview process separately from the post-interview decision flow.
  const isDecisionStage = (round) => {
    const n = String(round.roundName || "").toLowerCase();
    return (
      n.includes("decision") ||
      n.includes("offer") ||
      n.includes("hiring committee")
    );
  };
  const interviewRounds = rounds.filter((r) => !isDecisionStage(r));
  const decisionRounds = rounds.filter((r) => isDecisionStage(r));

  // Stats are about interview rounds only.
  const completed = interviewRounds.filter(
    (r) => r.status === InterviewRoundStatusEnum.Completed
  ).length;
  const inProgress = interviewRounds.filter(
    (r) =>
      r.status === InterviewRoundStatusEnum.SlotsProposed ||
      r.status === InterviewRoundStatusEnum.SlotSelected
  ).length;
  const pending = interviewRounds.filter(
    (r) => r.status === InterviewRoundStatusEnum.PendingSlotProposal
  ).length;
  // Per Sara (May-22): show the average rating across completed rounds so
  // HR doesn't have to mentally sum the per-round ratings. This is the
  // same value AI uses for "Interview Contribution" in the Score Breakdown.
  const completedWithRating = interviewRounds.filter(
    (r) =>
      r.status === InterviewRoundStatusEnum.Completed &&
      typeof r.rating === "number"
  );
  const averageRating =
    completedWithRating.length > 0
      ? completedWithRating.reduce((sum, r) => sum + (r.rating || 0), 0) /
        completedWithRating.length
      : null;

  return (
    <div style={{ padding: "1.5rem" }}>
      {/* Summary bar — labels explicit so per-round "Completed" badges aren't
          mistaken for "the whole process is complete". */}
      <div
        style={{
          background: "linear-gradient(135deg, #e6f4ea 0%, #f7faf7 100%)",
          borderRadius: "24px",
          padding: "14px 20px",
          marginBottom: "1.5rem",
          display: "flex",
          flexWrap: "wrap",
          gap: "24px",
          alignItems: "center",
          border: `1px solid ${theme.border}`,
        }}
      >
        <SummaryStat
          label="Interview rounds"
          value={`${completed} / ${interviewRounds.length}`}
          color={theme.accentDeep}
          sublabel="completed"
        />
        <SummaryStat
          label="Awaiting applicant"
          value={inProgress}
          color="#1677ff"
        />
        <SummaryStat
          label="HR action needed"
          value={pending}
          color="#c26b2c"
        />
        {averageRating != null && (
          <SummaryStat
            label="Average rating"
            value={`${averageRating.toFixed(1)} / 10`}
            color={
              averageRating >= 7
                ? "#15803d"
                : averageRating >= 5
                ? "#c26b2c"
                : "#b13e3e"
            }
            sublabel={`${completedWithRating.length} round${
              completedWithRating.length === 1 ? "" : "s"
            }`}
          />
        )}
        {/* Decision-stage stat removed — those rounds are now hidden entirely. */}
      </div>

      {/* Interview rounds */}
      {interviewRounds.length > 0 && (
        <>
          <SectionHeading
            title="Interview Rounds"
            count={interviewRounds.length}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1.2rem",
              marginBottom: "1.5rem",
            }}
          >
            {interviewRounds.map((round, idx) => (
              <RoundCard
                key={round.id || idx}
                round={round}
                // Per Mudassir: when the posting is Filled, no further hiring
                // pipeline actions (including Propose Slots / Mark Complete)
                // are allowed.
                onProposeSlots={
                  isPostingFilled ? null : () => setProposeRound(round)
                }
                onMarkComplete={
                  isPostingFilled ? null : () => setCompleteRound(round)
                }
              />
            ))}
          </div>
        </>
      )}

      {/* Per Sara: rounds 7 & 8 ("Hiring Committee / Final Decision" and
          "Offer Round") are seed-data noise — not real interview rounds.
          They're hidden from display entirely. If the backend round catalogue
          needs to change, that's Mudassir's task. */}

      <ProposeSlotsModal
        round={proposeRound}
        onCancel={() => setProposeRound(null)}
        onDone={() => {
          setProposeRound(null);
          fetchInterviewRounds();
        }}
      />
      <MarkCompleteModal
        round={completeRound}
        onCancel={() => setCompleteRound(null)}
        onDone={() => {
          setCompleteRound(null);
          fetchInterviewRounds();
        }}
      />
    </div>
  );
}

function SectionHeading({ title, count, subtitle }) {
  return (
    <div style={{ marginBottom: "12px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: "10px",
          flexWrap: "wrap",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: "1.05rem",
            fontWeight: 600,
            color: theme.accentDeep,
          }}
        >
          {title}
        </h3>
        <span
          style={{
            fontSize: "0.75rem",
            color: theme.textMuted,
            fontWeight: 600,
            background: theme.surface,
            padding: "2px 10px",
            borderRadius: "20px",
            border: `1px solid ${theme.border}`,
          }}
        >
          {count}
        </span>
      </div>
      {subtitle && (
        <div
          style={{
            fontSize: "0.78rem",
            color: theme.textLabel,
            marginTop: "4px",
            fontStyle: "italic",
          }}
        >
          {subtitle}
        </div>
      )}
    </div>
  );
}
SectionHeading.propTypes = {
  title: PropTypes.string,
  count: PropTypes.number,
  subtitle: PropTypes.string,
};

InterviewRoundsTab.propTypes = {
  jobApplicationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isRejected: PropTypes.bool,
  isPostingFilled: PropTypes.bool,
};

function SummaryStat({ label, value, color, sublabel }) {
  return (
    <div>
      <div
        style={{
          fontSize: "1.5rem",
          fontWeight: 700,
          color: color || theme.accentDeep,
          lineHeight: 1,
        }}
      >
        {value}
        {sublabel && (
          <span
            style={{
              fontSize: "0.75rem",
              fontWeight: 500,
              color: theme.textMuted,
              marginLeft: "6px",
            }}
          >
            {sublabel}
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: "0.7rem",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          color: theme.textLabel,
          marginTop: "4px",
          fontWeight: 600,
        }}
      >
        {label}
      </div>
    </div>
  );
}
SummaryStat.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.string,
  sublabel: PropTypes.string,
};

function RoundCard({ round, onProposeSlots, onMarkComplete }) {
  const roundTypeLabel = InterviewRoundTypeLabel[round.roundType] ?? "—";
  const statusLabel = InterviewRoundStatusLabel[round.status] ?? "Unknown";
  const statusColor = InterviewRoundStatusColor[round.status] ?? "#8c8c8c";

  return (
    <Card
      style={{ marginBottom: 0 }}
      accentColor={statusColor}
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <span>
            Round {round.roundNumber || "—"}: {round.roundName || roundTypeLabel}
          </span>
          <span
            style={{
              background: statusColor,
              color: "#fff",
              padding: "5px 14px",
              borderRadius: "40px",
              fontSize: "0.72rem",
              fontWeight: 600,
              letterSpacing: "0.3px",
              textTransform: "uppercase",
            }}
          >
            {statusLabel}
          </span>
        </div>
      }
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
          marginBottom: "12px",
        }}
      >
        <Pill variant="info">🎯 Type: {roundTypeLabel}</Pill>
      </div>
      <RoundBody round={round} />

      {/* HR actions per round status — hidden when handler is null
          (e.g., posting is Filled, so no further hiring actions allowed) */}
      {round.status === InterviewRoundStatusEnum.PendingSlotProposal && onProposeSlots && (
        <div style={{ marginTop: "14px", textAlign: "right" }}>
          <button type="button" onClick={onProposeSlots} style={primaryActionBtn}>
            📅 Propose Slots
          </button>
        </div>
      )}
      {round.status === InterviewRoundStatusEnum.SlotSelected && onMarkComplete && (
        <div style={{ marginTop: "14px", textAlign: "right" }}>
          <button type="button" onClick={onMarkComplete} style={primaryActionBtn}>
            ✅ Mark Complete
          </button>
        </div>
      )}
    </Card>
  );
}
RoundCard.propTypes = {
  round: PropTypes.object.isRequired,
  onProposeSlots: PropTypes.func,
  onMarkComplete: PropTypes.func,
};

const primaryActionBtn = {
  background: "#0f5a2a",
  color: "white",
  border: "none",
  padding: "8px 20px",
  borderRadius: "40px",
  fontSize: "0.82rem",
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
  boxShadow: "0 2px 6px rgba(15,90,42,0.18)",
};

function RoundBody({ round }) {
  if (round.status === InterviewRoundStatusEnum.PendingSlotProposal) {
    return (
      <div
        style={{
          background: "#fff7ed",
          borderRadius: "16px",
          padding: "12px 16px",
          fontSize: "0.88rem",
          color: "#92400e",
          borderLeft: "3px solid #f59e0b",
        }}
      >
        ⏳ Waiting for HR to propose interview slots.
      </div>
    );
  }

  if (round.status === InterviewRoundStatusEnum.SlotsProposed) {
    const slots = [round.slotOption1, round.slotOption2, round.slotOption3].filter(
      Boolean
    );
    return (
      <div>
        <LabelMeta>🗓️ Proposed slots (awaiting applicant)</LabelMeta>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            marginTop: "8px",
          }}
        >
          {slots.length === 0 ? (
            <div style={{ color: theme.textMuted, fontSize: "0.85rem" }}>
              No slots provided.
            </div>
          ) : (
            slots.map((slot, idx) => (
              <HighlightCard key={idx} accent="#1677ff" style={{ marginTop: 0 }}>
                <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                  Option {idx + 1}
                </div>
                <div style={{ fontSize: "0.85rem", color: theme.textMuted }}>
                  {formatDateTime(slot)}
                </div>
              </HighlightCard>
            ))
          )}
        </div>
      </div>
    );
  }

  if (round.status === InterviewRoundStatusEnum.SlotSelected) {
    return (
      <HighlightCard accent="#722ed1" style={{ marginTop: 0 }}>
        <LabelMeta>📅 Scheduled</LabelMeta>
        <div
          style={{
            fontSize: "1rem",
            fontWeight: 600,
            color: theme.accentDeep,
            marginTop: "4px",
          }}
        >
          {formatDateTime(round.selectedDateTime)}
        </div>
        <div
          style={{
            fontSize: "0.8rem",
            color: theme.textMuted,
            marginTop: "4px",
          }}
        >
          Applicant has selected this slot. Awaiting interview.
        </div>
      </HighlightCard>
    );
  }

  if (round.status === InterviewRoundStatusEnum.Completed) {
    return (
      <div>
        <HighlightCard accent="#2b7e3a" style={{ marginTop: 0 }}>
          <LabelMeta>✅ Conducted on</LabelMeta>
          <div
            style={{
              fontSize: "0.95rem",
              fontWeight: 600,
              color: theme.accentDeep,
              marginTop: "4px",
            }}
          >
            {formatDateTime(round.selectedDateTime)}
          </div>
        </HighlightCard>

        {typeof round.rating === "number" && (
          <div style={{ marginTop: "14px" }}>
            <LabelMeta>⭐ Rating</LabelMeta>
            <RatingBar value={round.rating} />
          </div>
        )}

        {round.summary && (
          <FeedbackBlock label="📝 Summary" value={round.summary} />
        )}
        {round.strengthsObserved && (
          <FeedbackBlock
            label="💪 Strengths observed"
            value={round.strengthsObserved}
            accent="#2b7e3a"
          />
        )}
        {round.concernsObserved && (
          <FeedbackBlock
            label="⚠️ Concerns observed"
            value={round.concernsObserved}
            accent="#c26b2c"
          />
        )}
        {round.notes && (
          <FeedbackBlock label="🗒️ HR notes" value={round.notes} />
        )}

        {/* Per Mudassir (May-23): backend now sets a dedicated `recordedOn`
            property when a round is marked complete. Prefer that over
            lastModifiedOn (which can be any edit). Hide the line entirely
            when neither is a real timestamp — the formatter returns "—"
            for DateTime.MinValue. */}
        {formatDateTime(round.recordedOn || round.lastModifiedOn) !== "—" && (
          <>
            <Divider />
            <div
              style={{
                fontSize: "0.72rem",
                color: theme.textLabel,
                fontFamily: "monospace",
              }}
            >
              🕒 Recorded:{" "}
              {formatDateTime(round.recordedOn || round.lastModifiedOn)}
            </div>
          </>
        )}
      </div>
    );
  }

  if (round.status === InterviewRoundStatusEnum.Cancelled) {
    return (
      <div
        style={{
          background: "#fef2f2",
          borderRadius: "16px",
          padding: "12px 16px",
          fontSize: "0.88rem",
          color: "#991b1b",
          borderLeft: "3px solid #ef4444",
        }}
      >
        ✗ This round was cancelled.
      </div>
    );
  }

  if (round.status === InterviewRoundStatusEnum.Skipped) {
    return (
      <div
        style={{
          background: "#f5f5f5",
          borderRadius: "16px",
          padding: "12px 16px",
          fontSize: "0.88rem",
          color: theme.textMuted,
          borderLeft: `3px solid ${theme.textMuted}`,
        }}
      >
        ⏭ This round was skipped.
      </div>
    );
  }

  return null;
}
RoundBody.propTypes = { round: PropTypes.object.isRequired };

function FeedbackBlock({ label, value, accent }) {
  return (
    <div style={{ marginTop: "12px" }}>
      <LabelMeta>{label}</LabelMeta>
      <div
        style={{
          background: "#fafcff",
          padding: "12px 14px",
          borderRadius: "16px",
          marginTop: "6px",
          fontSize: "0.88rem",
          lineHeight: 1.5,
          color: theme.text,
          borderLeft: `3px solid ${accent || "#7c9bc0"}`,
          whiteSpace: "pre-wrap",
        }}
      >
        {value}
      </div>
    </div>
  );
}
FeedbackBlock.propTypes = {
  label: PropTypes.node,
  value: PropTypes.string,
  accent: PropTypes.string,
};

function RatingBar({ value, max = 10 }) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const color = value >= 8 ? "#22c55e" : value >= 5 ? "#f59e0b" : "#ef4444";
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: "6px",
        }}
      >
        <span style={{ fontWeight: 700, fontSize: "1.4rem", color }}>{value}</span>
        <span style={{ fontSize: "0.8rem", color: theme.textMuted }}>/ {max}</span>
      </div>
      <div
        style={{
          background: "#e4eaf2",
          borderRadius: "20px",
          marginTop: "6px",
          height: "8px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            background: color,
            height: "100%",
            transition: "width 0.4s ease",
          }}
        />
      </div>
    </div>
  );
}
RatingBar.propTypes = { value: PropTypes.number, max: PropTypes.number };

// ============================================================
// HR ACTION MODALS
// ============================================================

function ProposeSlotsModal({ round, onCancel, onDone }) {
  const [submitting, setSubmitting] = useState(false);
  const [slot1, setSlot1] = useState("");
  const [slot2, setSlot2] = useState("");
  const [slot3, setSlot3] = useState("");

  useEffect(() => {
    if (round) {
      setSlot1("");
      setSlot2("");
      setSlot3("");
    }
  }, [round]);

  if (!round) return null;

  const handleSubmit = async () => {
    if (!slot1 || !slot2 || !slot3) {
      toast.warning("Please pick all 3 time slots for the applicant.");
      return;
    }
    try {
      setSubmitting(true);
      // Per Swagger: field is `jobInterviewRoundId`, NOT `interviewRoundId`.
      await axiosInstance.post(API.hiringInterviews.proposeSlots, {
        jobInterviewRoundId: round.id,
        slotOption1: new Date(slot1).toISOString(),
        slotOption2: new Date(slot2).toISOString(),
        slotOption3: new Date(slot3).toISOString(),
      });
      toast.success("Slots proposed. The applicant has been notified.");
      onDone();
    } catch (err) {
      console.error("Propose slots failed:", err);
      toast.error(
        err.response?.data?.details ||
          err.response?.data?.message ||
          "Failed to propose slots. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={!!round}
      title={`Propose Slots — Round ${round.roundNumber}: ${
        round.roundName || "Interview"
      }`}
      width={600}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={submitting}
          onClick={handleSubmit}
          style={{ background: "#0f5a2a", borderColor: "#0f5a2a" }}
        >
          Send to Applicant
        </Button>,
      ]}
    >
      <p
        style={{
          fontSize: "0.85rem",
          color: theme.textMuted,
          marginBottom: "12px",
        }}
      >
        Pick three datetimes. The applicant will choose one from these options.
      </p>
      <SlotInput label="Slot 1" value={slot1} onChange={setSlot1} />
      <SlotInput label="Slot 2" value={slot2} onChange={setSlot2} />
      <SlotInput label="Slot 3" value={slot3} onChange={setSlot3} />
    </Modal>
  );
}
ProposeSlotsModal.propTypes = {
  round: PropTypes.object,
  onCancel: PropTypes.func,
  onDone: PropTypes.func,
};

function SlotInput({ label, value, onChange }) {
  return (
    <div style={{ marginBottom: "12px" }}>
      <label
        style={{
          fontSize: "0.78rem",
          fontWeight: 600,
          color: theme.text,
          display: "block",
          marginBottom: "4px",
        }}
      >
        {label}
      </label>
      <input
        type="datetime-local"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          padding: "8px 12px",
          borderRadius: "8px",
          border: `1px solid ${theme.border}`,
          fontSize: "0.88rem",
          fontFamily: "inherit",
        }}
      />
    </div>
  );
}
SlotInput.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
};

function MarkCompleteModal({ round, onCancel, onDone }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    rating: 0,
    summary: "",
    strengthsObserved: "",
    concernsObserved: "",
    notes: "",
  });

  useEffect(() => {
    if (round) {
      setForm({
        rating: 0,
        summary: "",
        strengthsObserved: "",
        concernsObserved: "",
        notes: "",
      });
    }
  }, [round]);

  if (!round) return null;

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    if (!form.rating || form.rating < 1 || form.rating > 10) {
      toast.warning("Please rate the round between 1 and 10.");
      return;
    }
    let stopAILoading;
    try {
      setSubmitting(true);
      // Round-complete triggers AI re-scoring on the backend, so bump
      // timeout and show rotating loading messages.
      stopAILoading = startAILoading();
      // Per Swagger: field is `jobInterviewRoundId`. Also requires
      // discoveredSkills + verifiedSkillIds arrays (defaulted to empty
      // since we don't capture them in the UI yet).
      await axiosInstance.post(
        API.hiringInterviews.completeRound,
        {
          jobInterviewRoundId: round.id,
          discoveredSkills: [],
          verifiedSkillIds: [],
          rating: form.rating,
          summary: form.summary,
          strengthsObserved: form.strengthsObserved,
          concernsObserved: form.concernsObserved,
          notes: form.notes,
        },
        { timeout: AI_TIMEOUT }
      );
      toast.success("Round marked as completed.");
      onDone();
    } catch (err) {
      console.error("Mark complete failed:", err);
      toast.error(
        err.response?.data?.details ||
          err.response?.data?.message ||
          "Failed to mark round as complete. Please try again."
      );
    } finally {
      setSubmitting(false);
      if (stopAILoading) stopAILoading();
    }
  };

  return (
    <Modal
      open={!!round}
      title={`Mark Round Complete — Round ${round.roundNumber}: ${
        round.roundName || "Interview"
      }`}
      width={720}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={submitting}
          onClick={handleSubmit}
          style={{ background: "#0f5a2a", borderColor: "#0f5a2a" }}
        >
          Submit & Mark Complete
        </Button>,
      ]}
    >
      <div style={{ marginBottom: "14px" }}>
        <label
          style={{
            fontSize: "0.78rem",
            fontWeight: 600,
            color: theme.text,
            display: "block",
            marginBottom: "4px",
          }}
        >
          ⭐ Rating (1–10)
        </label>
        <InputNumber
          min={1}
          max={10}
          value={form.rating || null}
          onChange={(v) => update("rating", v)}
          style={{ width: "160px" }}
          placeholder="1–10"
        />
      </div>
      <div style={{ marginBottom: "12px" }}>
        <label
          style={{
            fontSize: "0.78rem",
            fontWeight: 600,
            color: theme.text,
            display: "block",
            marginBottom: "4px",
          }}
        >
          📝 Summary
        </label>
        <Input.TextArea
          rows={2}
          value={form.summary}
          onChange={(e) => update("summary", e.target.value)}
          placeholder="Short overall summary of this round"
        />
      </div>
      <div style={{ marginBottom: "12px" }}>
        <label
          style={{
            fontSize: "0.78rem",
            fontWeight: 600,
            color: theme.text,
            display: "block",
            marginBottom: "4px",
          }}
        >
          💪 Strengths observed
        </label>
        <Input.TextArea
          rows={2}
          value={form.strengthsObserved}
          onChange={(e) => update("strengthsObserved", e.target.value)}
        />
      </div>
      <div style={{ marginBottom: "12px" }}>
        <label
          style={{
            fontSize: "0.78rem",
            fontWeight: 600,
            color: theme.text,
            display: "block",
            marginBottom: "4px",
          }}
        >
          ⚠️ Concerns observed
        </label>
        <Input.TextArea
          rows={2}
          value={form.concernsObserved}
          onChange={(e) => update("concernsObserved", e.target.value)}
        />
      </div>
      <div>
        <label
          style={{
            fontSize: "0.78rem",
            fontWeight: 600,
            color: theme.text,
            display: "block",
            marginBottom: "4px",
          }}
        >
          🗒️ HR notes
        </label>
        <Input.TextArea
          rows={2}
          value={form.notes}
          onChange={(e) => update("notes", e.target.value)}
        />
      </div>
    </Modal>
  );
}
MarkCompleteModal.propTypes = {
  round: PropTypes.object,
  onCancel: PropTypes.func,
  onDone: PropTypes.func,
};

export default InterviewRoundsTab;
